import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { MallaCurricularDto } from "../../../dto/malla-curricular.dto";
import {
  AnioEscolar,
  GrupoContenido,
  mallaCurricularService,
  TemaContenido,
  UnidadContenido,
} from "../../../services/admin/malla-curricular";
import { AdminLayout } from "../components";
import { ContenidoCurricularForm } from "../components/ContenidoCurricularForm";

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
}

export const MallaCurricularPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingContent, setViewingContent] = useState<any>(null);
  const [mallas, setMallas] = useState<MallaCurricularDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Opciones para los dropdowns
  const [aniosEscolares, setAniosEscolares] = useState<AnioEscolar[]>([]);
  const [unidadesContenido, setUnidadesContenido] = useState<UnidadContenido[]>(
    []
  );
  const [temasContenido, setTemasContenido] = useState<TemaContenido[]>([]);
  const [gruposContenido, setGruposContenido] = useState<GrupoContenido[]>([]);

  // Estado para notificaciones
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
  });

  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  // Estado del formulario
  const [formData, setFormData] = useState<{
    anioEscolarId: number;
    unidadContenidoId: number;
    temaContenidoId: number;
    grupoContenidoId: number;
    conceptosPrevios: string;
    explicacion: string;
    ejemplo: string;
    solucion: string;
    dificultad: number;
    aplicacionPractica: string;
    fecha?: string;
    imagen?: File | undefined;
  }>({
    anioEscolarId: 0,
    unidadContenidoId: 0,
    temaContenidoId: 0,
    grupoContenidoId: 0,
    conceptosPrevios: "",
    explicacion: "",
    ejemplo: "",
    solucion: "",
    dificultad: 1,
    aplicacionPractica: "",
    fecha: "",
    imagen: undefined,
  });

  // Funciones para manejar notificaciones
  const showNotification = (
    type: "success" | "error" | "info" | "warning",
    title: string,
    message: string
  ) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }));
  };

  // Función para mostrar confirmación de eliminación
  const confirmDelete = (id: number) => {
    setItemToDelete(id);
    setShowDeleteConfirm(true);
  };

  // Función para cancelar eliminación
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos en paralelo
      const [mallasData, aniosData, unidadesData, temasData, gruposData] =
        await Promise.all([
          mallaCurricularService.getContenidosCurriculares(),
          mallaCurricularService.getAniosEscolares(),
          mallaCurricularService.getUnidadesContenido(),
          mallaCurricularService.getTemasContenido(),
          mallaCurricularService.getGruposContenido(),
        ]);

      setMallas(mallasData);
      setAniosEscolares(aniosData);
      setUnidadesContenido(unidadesData);
      setTemasContenido(temasData);
      setGruposContenido(gruposData);
    } catch (error: any) {
      console.error("Error cargando datos:", error);
      setError(error.message || "Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      anioEscolarId: 0,
      unidadContenidoId: 0,
      temaContenidoId: 0,
      grupoContenidoId: 0,
      conceptosPrevios: "",
      explicacion: "",
      ejemplo: "",
      solucion: "",
      dificultad: 1,
      aplicacionPractica: "",
      fecha: "",
      imagen: undefined,
    });
    setEditingId(null);
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Preparar datos para enviar, convirtiendo grupoContenidoId 0 a undefined
      const datosParaEnviar = {
        ...formData,
        grupoContenidoId:
          formData.grupoContenidoId === 0
            ? undefined
            : formData.grupoContenidoId,
      };

      if (isEditing) {
        // Actualizar contenido curricular existente
        await mallaCurricularService.actualizarContenidoCurricular(
          editingId!,
          datosParaEnviar
        );
        showNotification(
          "success",
          "Contenido curricular actualizado",
          "Contenido curricular actualizado exitosamente"
        );
        await cargarDatos(); // Recargar datos
        closeModal();
      } else {
        // Crear nuevo contenido curricular
        await mallaCurricularService.crearContenidoCurricular(datosParaEnviar);
        showNotification(
          "success",
          "Contenido curricular creado",
          "Contenido curricular creado exitosamente"
        );
        await cargarDatos(); // Recargar datos
        closeModal();
      }
    } catch (error: any) {
      console.error("Error:", error);
      showNotification(
        "error",
        "Error al procesar",
        error.message || "Error al procesar la solicitud"
      );
    }
  };

  const handleEdit = async (malla: MallaCurricularDto) => {
    try {
      if (!malla.id) {
        throw new Error("ID de la malla no encontrado");
      }

      // Obtener datos completos del contenido curricular para editar
      const contenidoCompleto =
        await mallaCurricularService.getContenidoCurricularById(malla.id);

      // Cargar datos en el formulario
      setFormData({
        anioEscolarId: contenidoCompleto.anioEscolar.idAnioEscolar,
        unidadContenidoId: contenidoCompleto.unidadContenido.idUnidadContenido,
        temaContenidoId: contenidoCompleto.temaContenido.idTemaContenido,
        grupoContenidoId:
          contenidoCompleto.grupoContenido?.idGrupoContenido || 0,
        conceptosPrevios: contenidoCompleto.conceptosPrevios || "",
        explicacion: contenidoCompleto.explicacion || "",
        ejemplo: contenidoCompleto.ejemplo || "",
        solucion: contenidoCompleto.solucion || "",
        dificultad: contenidoCompleto.dificultad || 1,
        aplicacionPractica: contenidoCompleto.aplicacionPractica || "",
        fecha: contenidoCompleto.fecha
          ? new Date(contenidoCompleto.fecha).toISOString().split("T")[0]
          : "",
        imagen: undefined, // Permitir seleccionar nueva imagen
      });

      setIsEditing(true);
      setEditingId(malla.id);
      setIsModalOpen(true);
    } catch (error: any) {
      console.error("Error cargando datos para editar:", error);
      showNotification(
        "error",
        "Error al cargar datos",
        error.message || "Error al cargar datos para editar"
      );
    }
  };

  const handleView = async (malla: MallaCurricularDto) => {
    try {
      if (!malla.id) {
        throw new Error("ID de la malla no encontrado");
      }

      // Obtener datos completos del contenido curricular para ver
      const contenidoCompleto =
        await mallaCurricularService.getContenidoCurricularById(malla.id);

      setViewingContent(contenidoCompleto);
      setIsViewModalOpen(true);
    } catch (error: any) {
      console.error("Error cargando datos para ver:", error);
      showNotification(
        "error",
        "Error al cargar datos",
        error.message || "Error al cargar datos para ver"
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await mallaCurricularService.eliminarContenidoCurricular(id);
      showNotification(
        "success",
        "Contenido curricular eliminado",
        "Contenido curricular eliminado exitosamente"
      );
      await cargarDatos(); // Recargar datos
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error: any) {
      showNotification(
        "error",
        "Error al eliminar",
        error.message || "Error al eliminar"
      );
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Funciones para paginación
  const totalPages = Math.ceil(mallas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMallas = mallas.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 w-full max-w-none">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-600">
              Malla Curricular
            </h1>
            <p className="text-gray-600">
              Gestiona el contenido curricular de Fracto
            </p>
          </div>
          <Button
            onClick={openCreateModal}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
          >
            Crear Nuevo
          </Button>
        </div>

        {/* Tabla de registros */}
        <Card className="rounded-xl shadow-lg border border-blue-100 w-full">
          <CardHeader className="border-b border-blue-100">
            <CardTitle className="text-gray-800">
              Mallas Curriculares ({mallas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-blue-200 table-fixed">
                <thead>
                  <tr className="bg-blue-50">
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/5">
                      Tema
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/10">
                      Nivel
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/10">
                      Unidad
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/4">
                      Descripción
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/10">
                      Dificultad
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-left font-semibold text-blue-800 w-1/10">
                      Fecha
                    </th>
                    <th className="border border-blue-200 px-6 py-4 text-center font-semibold text-blue-800 w-1/8">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8">
                        Cargando...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-red-600">
                        {error}
                      </td>
                    </tr>
                  ) : currentMallas.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8">
                        No hay mallas curriculares registradas.
                      </td>
                    </tr>
                  ) : (
                    currentMallas.map((malla: MallaCurricularDto) => (
                      <tr
                        key={malla.id}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="border border-blue-200 px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {malla.nombre}
                          </div>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                            {malla.nivel}
                          </span>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                            {malla.unidad}
                          </span>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <div
                            className="text-sm text-gray-600 max-w-xs truncate"
                            title={malla.descripcion}
                          >
                            {malla.descripcion}
                          </div>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              Number(malla.dificultad) === 1
                                ? "bg-green-100 text-green-800"
                                : Number(malla.dificultad) === 2
                                ? "bg-blue-100 text-blue-800"
                                : Number(malla.dificultad) === 3
                                ? "bg-yellow-100 text-yellow-800"
                                : Number(malla.dificultad) === 4
                                ? "bg-orange-100 text-orange-800"
                                : Number(malla.dificultad) === 5
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {Number(malla.dificultad) === 1
                              ? "Básica"
                              : Number(malla.dificultad) === 2
                              ? "Fácil"
                              : Number(malla.dificultad) === 3
                              ? "Media"
                              : Number(malla.dificultad) === 4
                              ? "Difícil"
                              : Number(malla.dificultad) === 5
                              ? "Muy Difícil"
                              : `Nivel ${malla.dificultad}`}
                          </span>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <span className="text-sm text-gray-500">
                            {malla.fechaCreacion}
                          </span>
                        </td>
                        <td className="border border-blue-200 px-6 py-4">
                          <div className="flex justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleView(malla)}
                              className="border-green-300 text-green-600 hover:bg-green-50 transition-colors"
                            >
                              Ver
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(malla)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => confirmDelete(malla.id!)}
                              className="border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                            >
                              Eliminar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {!loading && !error && mallas.length > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-blue-100">
                <div className="text-sm text-gray-600">
                  Mostrando {startIndex + 1} a{" "}
                  {Math.min(endIndex, mallas.length)} de {mallas.length}{" "}
                  registros
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                  >
                    Anterior
                  </Button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <Button
                          key={page}
                          size="sm"
                          variant={currentPage === page ? "default" : "outline"}
                          onClick={() => handlePageChange(page)}
                          className={
                            currentPage === page
                              ? "bg-blue-600 text-white"
                              : "border-blue-300 text-blue-600 hover:bg-blue-50"
                          }
                        >
                          {page}
                        </Button>
                      )
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={
            isEditing
              ? "Editar Contenido Curricular"
              : "Crear Nuevo Contenido Curricular"
          }
        >
          <ContenidoCurricularForm
            isEditing={isEditing}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={closeModal}
            aniosEscolares={aniosEscolares}
            unidadesContenido={unidadesContenido}
            temasContenido={temasContenido}
            gruposContenido={gruposContenido}
          />
        </Modal>

        {/* Notificación */}
        <Notification
          isOpen={notification.isOpen}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={closeNotification}
        />

        {/* Modal para ver contenido completo */}
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          title="Ver Contenido Curricular"
        >
          {viewingContent && (
            <div className="space-y-6">
              {/* Información básica */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Año Escolar
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.anioEscolar?.nombre}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidad
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.unidadContenido?.nombre}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tema
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.temaContenido?.nombre}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grupo
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.grupoContenido?.nombre || "No especificado"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dificultad
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.dificultad || "No especificada"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {viewingContent.fecha
                      ? new Date(viewingContent.fecha).toLocaleDateString()
                      : "No especificada"}
                  </p>
                </div>
              </div>

              {/* Conceptos previos */}
              {viewingContent.conceptosPrevios && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conceptos Previos
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {viewingContent.conceptosPrevios}
                  </p>
                </div>
              )}

              {/* Explicación */}
              {viewingContent.explicacion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Explicación
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {viewingContent.explicacion}
                  </p>
                </div>
              )}

              {/* Ejemplo */}
              {viewingContent.ejemplo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ejemplo
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {viewingContent.ejemplo}
                  </p>
                </div>
              )}

              {/* Solución */}
              {viewingContent.solucion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Solución
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {viewingContent.solucion}
                  </p>
                </div>
              )}

              {/* Aplicación práctica */}
              {viewingContent.aplicacionPractica && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aplicación Práctica
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                    {viewingContent.aplicacionPractica}
                  </p>
                </div>
              )}

              {/* Imagen */}
              {viewingContent.imagenUrl && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imagen
                  </label>
                  <div className="bg-gray-50 p-3 rounded">
                    <img
                      src={viewingContent.imagenUrl}
                      alt="Imagen del contenido"
                      className="max-w-full h-auto max-h-96 rounded shadow-sm"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = "none";
                        const errorDiv =
                          target.nextElementSibling as HTMLElement;
                        if (errorDiv) {
                          errorDiv.style.display = "block";
                        }
                      }}
                    />
                    <div
                      className="hidden text-sm text-gray-500 text-center py-4"
                      style={{ display: "none" }}
                    >
                      Error al cargar la imagen
                    </div>
                  </div>
                </div>
              )}

              {/* Botón cerrar */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setIsViewModalOpen(false)}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 border border-blue-100">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Confirmar eliminación
                    </h3>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar este contenido
                    curricular? Esta acción no se puede deshacer.
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDelete(itemToDelete!)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
                  >
                    Eliminar
                  </button>
                  <button
                    onClick={cancelDelete}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
